import type { IPublicClientApplication } from '@azure/msal-browser';
import axios, { type AxiosInstance } from 'axios';

function withApiVersion(baseUrl: string): string {
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return normalizedBase.endsWith('/v1')
    ? normalizedBase
    : `${normalizedBase}/v1`;
}

async function tryAcquireToken(
  msalInstance: IPublicClientApplication,
  scopes: string[],
): Promise<string | null> {
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

  if (!account || scopes.length === 0) {
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      account,
      scopes,
    });
    return response.accessToken;
  } catch {
    return null;
  }
}

export function createAuthenticatedAxios(
  msalInstance: IPublicClientApplication,
  baseUrl: string,
  scopes: string[],
): AxiosInstance {
  const axiosInstance = axios.create({
    baseURL: withApiVersion(baseUrl),
    validateStatus: (status) =>
      (status >= 200 && status < 300) || status === 401,
  });

  axiosInstance.interceptors.request.use(async (config) => {
    const token = await tryAcquireToken(msalInstance, scopes);
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axiosInstance.interceptors.response.use((response) => {
    if (response.status === 401) {
      throw new Response(null, { status: 401 });
    }
    return response;
  });

  return axiosInstance;
}
